import { Box, FilterListBox, Heading, Notice, Progress, Stack } from '@eleveo/components-react';
import { WithT } from 'i18next';
import { equals } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';

import { ASSIGN_SHIFT_CONTAINER_TAG, ASSIGN_SHIFT_FILTER_BOX_TAG, ASSIGN_SHIFT_HEADING_TAG } from './constants';
import { Center } from '../../components/LayoutNext/components/Center';
import { Agent, ShiftTemplate } from '../../graphql/components';
import { recordBy } from '../../utils/array';
import { fullNameOfAgent } from '../../utils/helpers';

export type ShiftTemplatesBulkAssignFormProps = {
  agentIdsToAssign: string[] | null;
  agentsAll: Agent[] | null;
  loading: boolean;
  setAgentIdsToAssign: React.Dispatch<React.SetStateAction<string[] | null>>;
  shiftTemplateId?: string;
  shiftTemplates: ShiftTemplate[];
  effectiveDate?: string;
} & WithT;

export const ShiftTemplatesBulkAssignForm: React.FC<ShiftTemplatesBulkAssignFormProps> = ({
  agentIdsToAssign,
  agentsAll,
  loading,
  setAgentIdsToAssign,
  shiftTemplateId,
  shiftTemplates,
  effectiveDate,
  t,
}) => {
  const shiftTemplatesObj = recordBy(shiftTemplates, 'id');

  const suitableAgents = useMemo(
    () =>
      (agentsAll ?? [])
        .filter((agent) => {
          // Check if the agent has the selected shiftTemplateId assigned
          if (shiftTemplateId && agent.shiftTemplateIds?.includes(shiftTemplateId)) {
            // If the agent already has the selected shiftTemplateId, agent is suitable
            return true;
          }

          // Check if there is an entered effectiveDate to compare against
          if (effectiveDate) {
            // Get all effectiveDates for the shift templates the agent already has
            const agentShiftTemplatesEffectiveDates = agent.shiftTemplateIds?.map(
              (agentShiftTemplateId) => shiftTemplatesObj[agentShiftTemplateId]?.effectiveDate
            );

            // If any of the agent's effectiveDates match the new effectiveDate, the agent is not suitable
            if (agentShiftTemplatesEffectiveDates?.includes(effectiveDate)) {
              return false;
            }
          }

          // If none of the above conditions apply, the agent is suitable
          return true;
        })
        .map((agent) => ({
          ...agent,
          name: fullNameOfAgent(agent),
        })),
    [agentsAll, effectiveDate, shiftTemplatesObj, shiftTemplateId]
  );

  const alreadyAssignedAgentsIds = useMemo(
    () =>
      shiftTemplateId
        ? (agentsAll ?? []).filter((agent) => agent.shiftTemplateIds?.includes(shiftTemplateId)).map(({ id }) => id)
        : [],
    [agentsAll, shiftTemplateId]
  );

  // Check if there are any new agents to assign, then filter these agents, retaining only those that belong to agents considered suitable
  useEffect(() => {
    // Check if there are any new agent IDs to assign into template
    if (agentIdsToAssign?.length) {
      // Get the IDs of all suitable agents
      const suitableAgentIds = suitableAgents.map((suitableAgent) => suitableAgent.id);

      // Filter out the agent IDs that are not in the list of suitable agents
      const filteredAgentIdsToAssign = agentIdsToAssign.filter((agentIdToAssign) =>
        suitableAgentIds.includes(agentIdToAssign)
      );

      // Update the state with the filtered list of agent IDs to assign
      setAgentIdsToAssign(filteredAgentIdsToAssign);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDate]);

  const assignedAgentsIds = agentIdsToAssign || alreadyAssignedAgentsIds;

  const setAgentsWithEqualsCheck = (agentIds: Set<React.Key>) => {
    // To disable save button when agents to assign are equal to initial agents
    const agentIdsSorted = [...agentIds].sort() as string[];
    setAgentIdsToAssign(equals(agentIdsSorted, alreadyAssignedAgentsIds.sort()) ? null : agentIdsSorted);
  };

  return loading ? (
    <Center>
      <Progress />
    </Center>
  ) : (
    <Box padding={20} testId={ASSIGN_SHIFT_CONTAINER_TAG}>
      <Stack direction="vertical" className="w-full" spacing={25}>
        <Heading as="h3" className="text-blue-700" id={ASSIGN_SHIFT_HEADING_TAG}>
          {t('person.availablePeople')}
        </Heading>

        <Notice>
          <Trans
            i18nKey="shiftTemplate.availablePeopleToAssign"
            components={[<strong key="0">available people</strong>]}
          />
        </Notice>

        <FilterListBox
          aria-labelledby={ASSIGN_SHIFT_HEADING_TAG}
          isBig
          items={suitableAgents}
          onSelectionChange={setAgentsWithEqualsCheck}
          selectionMode="multiple"
          selectedKeys={assignedAgentsIds}
          testId={ASSIGN_SHIFT_FILTER_BOX_TAG}
        />
      </Stack>
    </Box>
  );
};
